<script>
import { apiSecond } from '@/boot/axios'
import * as XLSX from 'xlsx'

const FILTERS = ['today', 'week', 'month', 'quarter', 'year', 'period']

export default {
	name: 'SpvCheckTirComparison',
	data() {
		return {
			filter: 'month',
			from: '',
			to: '',
			loading: false,
			exporting: false,
			data: null,
			expanded: {},
			filters: FILTERS,
		}
	},
	computed: {
		summary() {
			return (
				(this.data && this.data.summary) || {
					spv_total: 0,
					checks_total: 0,
					verified_total: 0,
					no_check_total: 0,
					pending_total: 0,
					delta_total: 0,
					coverage: 0,
				}
			)
		},
		period() {
			return (this.data && this.data.period) || { from: '', to: '', filter: this.filter }
		},
		classifiedGroups() {
			const groups = (this.data && this.data.groups) || []
			return groups.filter((g) => g.basin !== 'Неизвестно')
		},
		unclassifiedGroup() {
			const groups = (this.data && this.data.groups) || []
			return groups.find((g) => g.basin === 'Неизвестно') || null
		},
	},
	mounted() {
		this.fetch()
	},
	methods: {
		coverageClass(v) {
			const n = Number(v) || 0
			if (n >= 90) return 'cov--green'
			if (n >= 70) return 'cov--yellow'
			return 'cov--red'
		},
		deltaClass(v) {
			return Number(v) > 0 ? 'delta--red' : ''
		},
		fmt(n) {
			if (n === null || n === undefined) return '0'
			return new Intl.NumberFormat('ru-RU').format(n)
		},
		fmtCoverage(n) {
			const v = Number(n) || 0
			return v.toFixed(1).replace('.', ',') + '%'
		},
		fmtDate(d) {
			if (!d) return ''
			const parts = d.split('-')
			if (parts.length !== 3) return d
			return `${parts[2]}.${parts[1]}.${parts[0]}`
		},
		buildParams() {
			const params = { filter: this.filter }
			if (this.filter === 'period') {
				if (this.from) params.from = this.from
				if (this.to) params.to = this.to
			}
			return params
		},
		selectFilter(f) {
			this.filter = f
			if (f !== 'period') {
				this.fetch()
			}
		},
		applyPeriod() {
			if (!this.from || !this.to) return
			this.fetch()
		},
		toggle(basin) {
			this.$set(this.expanded, basin, !this.expanded[basin])
		},
		async fetch() {
			try {
				this.loading = true
				const response = await apiSecond.get('/v1/spv-check/comparison', {
					params: this.buildParams(),
				})
				if (response && response.data && response.data.data) {
					this.data = response.data.data
					if (this.data.period) {
						if (this.data.period.filter) this.filter = this.data.period.filter
						if (this.data.period.from) this.from = this.data.period.from
						if (this.data.period.to) this.to = this.data.period.to
					}
				}
			} catch (e) {
				if (e && e.response && e.response.status === 401) {
					this.$router.push('/' + this.$i18n.locale + '/login')
				}
			} finally {
				this.loading = false
			}
		},
		async exportExcel() {
			try {
				this.exporting = true

				// Данные для отчёта уже приходят в JSON — формируем Excel на клиенте,
				// чтобы не зависеть от серверного экспорта.
				if (!this.data) {
					await this.fetch()
				}
				if (!this.data) return

				const round1 = (n) => Math.round((Number(n) || 0) * 10) / 10

				const header = [
					this.$t('spv_comparison.col_basin_region'),
					this.$t('spv_comparison.col_spv'),
					this.$t('spv_comparison.col_verified'),
					this.$t('spv_comparison.col_no_check'),
					this.$t('spv_comparison.col_pending'),
					this.$t('spv_comparison.col_coverage'),
				]

				const rows = [header]

				const pushGroup = (group) => {
					rows.push([
						group.basin,
						Number(group.spv) || 0,
						Number(group.verified) || 0,
						Number(group.no_check) || 0,
						Number(group.pending) || 0,
						round1(group.coverage),
					])
					;(group.regions || []).forEach((region) => {
						rows.push([
							'    ' + region.region,
							Number(region.spv) || 0,
							Number(region.verified) || 0,
							Number(region.no_check) || 0,
							Number(region.pending) || 0,
							round1(region.coverage),
						])
					})
				}

				this.classifiedGroups.forEach(pushGroup)
				if (this.unclassifiedGroup) pushGroup(this.unclassifiedGroup)

				rows.push([
					this.$t('spv_comparison.total'),
					Number(this.summary.spv_total) || 0,
					Number(this.summary.verified_total) || 0,
					Number(this.summary.no_check_total) || 0,
					Number(this.summary.pending_total) || 0,
					round1(this.summary.coverage),
				])

				const worksheet = XLSX.utils.aoa_to_sheet(rows)
				worksheet['!cols'] = [
					{ wch: 40 },
					{ wch: 14 },
					{ wch: 14 },
					{ wch: 14 },
					{ wch: 22 },
					{ wch: 14 },
				]

				const workbook = XLSX.utils.book_new()
				XLSX.utils.book_append_sheet(workbook, worksheet, 'SPV-check')

				XLSX.writeFile(
					workbook,
					`spv-comparison-${this.period.from || ''}_${this.period.to || ''}.xlsx`,
				)
			} catch (e) {
				// silent
			} finally {
				this.exporting = false
			}
		},
	},
}
</script>

<template>
	<div class="spv-comparison">
		<!-- Period filter row -->
		<div class="cmp-toolbar">
			<div class="cmp-period-btns">
				<button
					v-for="f in filters"
					:key="f"
					type="button"
					class="cmp-period-btn"
					:class="{ 'cmp-period-btn--active': filter === f }"
					@click="selectFilter(f)"
				>
					{{ $t('spv_comparison.period_' + f) }}
				</button>
			</div>

			<div v-if="filter === 'period'" class="cmp-period-range">
				<input type="date" v-model="from" class="cmp-date-input" />
				<span class="cmp-dash">—</span>
				<input type="date" v-model="to" class="cmp-date-input" />
				<button
					type="button"
					class="cmp-btn cmp-btn--primary"
					:disabled="!from || !to"
					@click="applyPeriod"
				>
					{{ $t('button.apply') || 'Применить' }}
				</button>
			</div>

			<div class="cmp-toolbar-right">
				<div class="cmp-range-badge" v-if="period.from && period.to">
					{{ fmtDate(period.from) }} — {{ fmtDate(period.to) }}
				</div>
				<button type="button" class="cmp-btn cmp-btn--secondary" :disabled="loading" @click="fetch">
					{{ $t('button.refresh') || 'Обновить' }}
				</button>
				<button
					type="button"
					class="cmp-btn cmp-btn--green"
					:disabled="exporting || loading"
					@click="exportExcel"
				>
					{{ $t('spv_comparison.export_excel') }}
				</button>
			</div>
		</div>

		<!-- Summary cards -->
		<div class="cmp-summary">
			<div class="cmp-card cmp-card--blue">
				<div class="cmp-card__label">{{ $t('spv_comparison.spv_total') }}</div>
				<div class="cmp-card__value">{{ fmt(summary.spv_total) }}</div>
			</div>
			<div class="cmp-card cmp-card--green">
				<div class="cmp-card__label">{{ $t('spv_comparison.verified_total') }}</div>
				<div class="cmp-card__value">
					{{ fmt(summary.verified_total) }}
					<span class="cmp-cov-badge" :class="coverageClass(summary.coverage)">
						{{ fmtCoverage(summary.coverage) }} {{ $t('spv_comparison.coverage_short') }}
					</span>
				</div>
			</div>
			<div class="cmp-card cmp-card--red">
				<div class="cmp-card__label">{{ $t('spv_comparison.no_check_total') }}</div>
				<div class="cmp-card__value">{{ fmt(summary.no_check_total) }}</div>
				<div class="cmp-card__sub">
					{{ $t('spv_comparison.pending_total') }}: <b>{{ fmt(summary.pending_total) }}</b>
				</div>
			</div>
		</div>
		<div class="cmp-hint">{{ $t('spv_comparison.coverage_hint') }}</div>

		<!-- Table -->
		<div class="cmp-table-wrap">
			<table class="cmp-table">
				<thead>
					<tr>
						<th class="cmp-th-name">{{ $t('spv_comparison.col_basin_region') }}</th>
						<th class="cmp-th-num">{{ $t('spv_comparison.col_spv') }}</th>
						<th class="cmp-th-num">{{ $t('spv_comparison.col_verified') }}</th>
						<th class="cmp-th-num">{{ $t('spv_comparison.col_no_check') }}</th>
						<th class="cmp-th-num">{{ $t('spv_comparison.col_pending') }}</th>
						<th class="cmp-th-num">{{ $t('spv_comparison.col_coverage') }}</th>
					</tr>
				</thead>
				<tbody v-if="loading">
					<tr>
						<td colspan="6" class="cmp-td-empty">
							{{ $t('system_message.loading') || 'Загрузка…' }}
						</td>
					</tr>
				</tbody>
				<tbody v-else-if="!classifiedGroups.length && !unclassifiedGroup">
					<tr>
						<td colspan="6" class="cmp-td-empty">
							{{ $t('system_message.no_data') || 'Нет данных' }}
						</td>
					</tr>
				</tbody>
				<tbody v-else>
					<template v-for="group in classifiedGroups">
						<tr :key="'b-' + group.basin" class="cmp-row-basin" @click="toggle(group.basin)">
							<td class="cmp-td-name">
								<span class="cmp-caret" :class="{ 'cmp-caret--open': expanded[group.basin] }"
									>▶</span
								>
								<b>{{ group.basin }}</b>
							</td>
							<td class="cmp-td-num">{{ fmt(group.spv) }}</td>
							<td class="cmp-td-num">{{ fmt(group.verified) }}</td>
							<td class="cmp-td-num" :class="deltaClass(group.no_check)">
								{{ fmt(group.no_check) }}
							</td>
							<td class="cmp-td-num cmp-td-muted">{{ fmt(group.pending) }}</td>
							<td class="cmp-td-num">
								<span class="cmp-cov-badge" :class="coverageClass(group.coverage)">
									{{ fmtCoverage(group.coverage) }}
								</span>
							</td>
						</tr>
						<tr
							v-for="region in group.regions"
							v-if="expanded[group.basin]"
							:key="'r-' + group.basin + '-' + region.region"
							class="cmp-row-region"
						>
							<td class="cmp-td-name cmp-td-name--indent">{{ region.region }}</td>
							<td class="cmp-td-num">{{ fmt(region.spv) }}</td>
							<td class="cmp-td-num">{{ fmt(region.verified) }}</td>
							<td class="cmp-td-num" :class="deltaClass(region.no_check)">
								{{ fmt(region.no_check) }}
							</td>
							<td class="cmp-td-num cmp-td-muted">{{ fmt(region.pending) }}</td>
							<td class="cmp-td-num">
								<span class="cmp-cov-badge" :class="coverageClass(region.coverage)">
									{{ fmtCoverage(region.coverage) }}
								</span>
							</td>
						</tr>
					</template>

					<tr class="cmp-row-total">
						<td class="cmp-td-name">
							<b>{{ $t('spv_comparison.total') }}</b>
						</td>
						<td class="cmp-td-num">
							<b>{{ fmt(summary.spv_total) }}</b>
						</td>
						<td class="cmp-td-num">
							<b>{{ fmt(summary.verified_total) }}</b>
						</td>
						<td class="cmp-td-num" :class="deltaClass(summary.no_check_total)">
							<b>{{ fmt(summary.no_check_total) }}</b>
						</td>
						<td class="cmp-td-num cmp-td-muted">
							<b>{{ fmt(summary.pending_total) }}</b>
						</td>
						<td class="cmp-td-num">
							<span class="cmp-cov-badge" :class="coverageClass(summary.coverage)">
								<b>{{ fmtCoverage(summary.coverage) }}</b>
							</span>
						</td>
					</tr>
				</tbody>
			</table>
		</div>
	</div>
</template>

<style scoped>
.spv-comparison {
	font-family: 'Montserrat', sans-serif;
}

/* Toolbar */
.cmp-toolbar {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 12px;
	margin-bottom: 20px;
}

.cmp-period-btns {
	display: inline-flex;
	flex-wrap: wrap;
	background: #fbfbfb;
	border-radius: 30px;
	padding: 4px;
	gap: 2px;
}

.cmp-period-btn {
	border: none;
	background: transparent;
	padding: 8px 16px;
	border-radius: 24px;
	font-size: 14px;
	font-weight: 500;
	color: #52a5fc;
	cursor: pointer;
	transition: all 0.2s ease;
}

.cmp-period-btn:hover {
	background: #eaf4ff;
}

.cmp-period-btn--active {
	background: #53a6fc;
	color: #fff;
}

.cmp-period-range {
	display: inline-flex;
	align-items: center;
	gap: 8px;
}

.cmp-date-input {
	border: 1px solid #d3e7fb;
	border-radius: 6px;
	padding: 8px 10px;
	font-size: 14px;
	color: #0a091d;
	background: #fff;
}

.cmp-dash {
	color: #6c757d;
}

.cmp-toolbar-right {
	margin-left: auto;
	display: inline-flex;
	align-items: center;
	gap: 10px;
	flex-wrap: wrap;
}

.cmp-range-badge {
	background: #eaf4ff;
	color: #24324a;
	padding: 6px 12px;
	border-radius: 20px;
	font-size: 13px;
	font-weight: 500;
}

.cmp-btn {
	border-radius: 24px;
	padding: 8px 18px;
	border: none;
	font-size: 14px;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.2s ease;
	color: #fff;
}

.cmp-btn:disabled {
	opacity: 0.6;
	cursor: not-allowed;
}

.cmp-btn--primary {
	background: #2196f3;
}

.cmp-btn--primary:hover:not(:disabled) {
	background: #1976d2;
}

.cmp-btn--secondary {
	background: #53a6fc;
}

.cmp-btn--secondary:hover:not(:disabled) {
	background: #6aabf1;
}

.cmp-btn--green {
	background: #28a745;
}

.cmp-btn--green:hover:not(:disabled) {
	background: #218838;
}

/* Summary */
.cmp-summary {
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	gap: 16px;
	margin-bottom: 12px;
}

.cmp-hint {
	margin-bottom: 24px;
	font-size: 12px;
	color: #6c757d;
}

.cmp-card {
	border-radius: 16px;
	padding: 20px 24px;
	box-shadow: 0 6px 18px rgba(20, 34, 66, 0.08);
}

.cmp-card--blue {
	background: linear-gradient(135deg, rgba(209, 233, 252, 0.9) 0%, rgba(132, 196, 255, 0.6) 100%);
}

.cmp-card--green {
	background: linear-gradient(135deg, rgba(222, 255, 233, 0.9) 0%, rgba(215, 252, 240, 0.8) 100%);
}

.cmp-card--red {
	background: linear-gradient(135deg, rgba(255, 228, 228, 0.9) 0%, rgba(255, 213, 213, 0.8) 100%);
}

.cmp-card__label {
	font-size: 14px;
	color: #4a5568;
	margin-bottom: 8px;
}

.cmp-card__value {
	font-size: 32px;
	font-weight: 700;
	color: #1f2937;
	display: flex;
	align-items: center;
	gap: 12px;
	flex-wrap: wrap;
}

.cmp-card__sub {
	margin-top: 10px;
	font-size: 13px;
	color: #4a5568;
}

/* Coverage badge */
.cmp-cov-badge {
	display: inline-block;
	padding: 4px 10px;
	border-radius: 999px;
	font-size: 13px;
	font-weight: 600;
}

.cov--green {
	background: #d4edda;
	color: #155724;
}

.cov--yellow {
	background: #fff3cd;
	color: #856404;
}

.cov--red {
	background: #f8d7da;
	color: #721c24;
}

.delta--red {
	color: #dc3545;
	font-weight: 600;
}

/* Table */
.cmp-table-wrap {
	background: #fff;
	border-radius: 8px;
	overflow: hidden;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
	overflow-x: auto;
}

.cmp-table {
	width: 100%;
	border-collapse: collapse;
	font-size: 14px;
}

.cmp-table thead {
	background: #f8f9fa;
}

.cmp-table th {
	padding: 12px 16px;
	font-weight: 600;
	color: #333;
	border-bottom: 1px solid #e6e8ec;
	text-align: left;
}

.cmp-th-num {
	text-align: center;
}

.cmp-table td {
	padding: 12px 16px;
	border-bottom: 1px solid #f0f0f0;
	color: #333;
}

.cmp-td-num {
	text-align: left;
	white-space: nowrap;
}

.cmp-td-muted {
	color: #6c757d;
}

.cmp-td-name--indent {
	padding-left: 44px;
}

.cmp-row-basin {
	cursor: pointer;
	background: #fbfbfd;
}

.cmp-row-basin:hover {
	background: #f0f6ff;
}

.cmp-caret {
	display: inline-block;
	width: 14px;
	color: #52a5fc;
	margin-right: 8px;
	transition: transform 0.2s ease;
	font-size: 10px;
}

.cmp-caret--open {
	transform: rotate(90deg);
}

.cmp-row-region td {
	background: #fff;
	color: #555;
}

.cmp-row-total td {
	background: #eaf4ff;
	border-top: 2px solid #53a6fc;
	border-bottom: none;
	font-size: 15px;
}

.cmp-row-unclassified td {
	background: #fff8e1;
	border-top: 1px dashed #f4b400;
}

.cmp-warn-icon {
	margin-right: 6px;
}

.cmp-td-hint {
	font-size: 12px;
	color: #856404;
	font-weight: 400;
	margin-top: 4px;
}

.cmp-td-empty {
	text-align: center;
	padding: 32px 16px;
	color: #6c757d;
}

@media (max-width: 768px) {
	.cmp-summary {
		grid-template-columns: 1fr;
	}
}
</style>
